import { useEffect, useRef, type RefObject } from 'react';

export type KeyboardShortcut = {
    keys: string[];
    handler: () => void;
};

const TYPING_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];

function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return TYPING_TAGS.includes(target.tagName) || target.isContentEditable;
}

function isBrowserOrOsShortcut(event: KeyboardEvent): boolean {
    return event.metaKey || event.ctrlKey || event.altKey;
}

export function isInTopLayer(owner: HTMLElement): boolean {
    const dialogs = document.querySelectorAll('[role="dialog"]');
    const top = dialogs[dialogs.length - 1];
    return top === undefined || top.contains(owner);
}

export type KeyboardShortcutsOptions = {
    shortcuts: KeyboardShortcut[];
    enabled: boolean;
    ownerRef: RefObject<HTMLElement | null>;
};

export function useKeyboardShortcuts({ shortcuts, enabled, ownerRef }: KeyboardShortcutsOptions): void {
    const latestShortcuts = useRef(shortcuts);
    latestShortcuts.current = shortcuts;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const belongsToSomeoneElse = (event: KeyboardEvent): boolean => {
            const owner = ownerRef.current;
            return (
                event.defaultPrevented ||
                isBrowserOrOsShortcut(event) ||
                isTypingTarget(event.target) ||
                owner === null ||
                !isInTopLayer(owner)
            );
        };

        const shortcutFor = (event: KeyboardEvent): KeyboardShortcut | undefined =>
            latestShortcuts.current.find(({ keys }) => keys.includes(event.key));

        const handleKeyDown = (event: KeyboardEvent) => {
            if (belongsToSomeoneElse(event)) {
                return;
            }
            const shortcut = shortcutFor(event);
            if (!shortcut) {
                return;
            }
            event.preventDefault();
            shortcut.handler();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [enabled, ownerRef]);
}
